import React, { useState, useRef, useEffect } from 'react';
import Toast from './Toast';
import countries from '../Assets/Countries.json';
import { db, auth, storage } from '../firebaseConfig';
import { doc, getDoc, collection, getDocs, updateDoc, deleteField, Timestamp, query, where, limit, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useParams } from 'react-router-dom';
import { useSubAgentAuth } from '../pages/Clients/SubAgent/SubAgentAuthContext';
import ConfirmationModal from './modals/ConfirmationModalActions';

import { setPipelineRefresh } from '../redux/pipelineRefresh';
import { useAppSelector, useAppDispatch } from '../redux/store';


interface DropdownRefs {
    status?: HTMLDivElement;
    welcomeCallByAdmissionTeam?: HTMLDivElement;
    fileSubmissionStatus?: HTMLDivElement;
    applicationMoved?: HTMLDivElement;
    conditionalOffer?: HTMLDivElement;
    unconditionalOffer?: HTMLDivElement;
    feePaymentStatus?: HTMLDivElement;
    fileMovedToVisa?: HTMLDivElement;
    country?: HTMLDivElement;
    university?: HTMLDivElement;
    [key: string]: HTMLDivElement | undefined;
}

type DropdownSetter = React.Dispatch<React.SetStateAction<boolean>>;

interface DropdownSetters {
    [key: string]: DropdownSetter;
}

interface AdmissionStatus {
    id: string;
    admissionStatus: string;
}

interface FileSubmissionStatus {
    id: string;
    fileSubmissionStatus: string;
}

interface ApplicationMovedStatus {
    id: string;
    applicationMovedStatus: string;
}

interface OfferLetterStatus {
    id: string;
    offerLetterStatus: string;
}

interface FeePaymentStatus {
    id: string;
    feePaymentStatus: string;
}

interface VisaFileProcessingStatus {
    id: string;
    visaFileProcessingStatus: string;
}

interface WelcomeCallByAdmissionTeamStatus {
    id: string;
    welcomeCallByAdmissionTeamStatus: string;
}

interface Country {
    id: string;
    name: string;
}

interface University {
    id: string;
    name: string;
    uniCode: string;
}

interface AdmissionDetailsProps {
    write: boolean;
}

const AdmissionDetails = ({ write }: AdmissionDetailsProps) => {

    const { currentSubAgent } = useSubAgentAuth();

    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const [isWelcomeCallByAdmissionTeamDropdownOpen, setIsWelcomeCallByAdmissionTeamDropdownOpen] = useState(false);
    const [welcomeCallSearchTerm, setWelcomeCallSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [agencyName, setAgencyName] = useState('');
    const [programName, setProgramName] = useState('');
    const [programRemarks, setProgramRemarks] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedCountryName, setSelectedCountryName] = useState('');
    const [countrySearchTerm, setCountrySearchTerm] = useState('');
    const [isLoadingCountry, setIsLoadingCountry] = useState(false);
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
    const [isFileSubmissionDropdownOpen, setIsFileSubmissionDropdownOpen] = useState(false);
    const [fileSubmissionSearchTerm, setFileSubmissionSearchTerm] = useState('');
    const [fileSubmissionStatus, setFileSubmissionStatus] = useState('');
    const [isApplicationMovedDropdownOpen, setIsApplicationMovedDropdownOpen] = useState(false);
    const [applicationMoved, setApplicationMoved] = useState('');
    const [finalRemarks, setFinalRemarks] = useState('');
    const [appliedUniversityId, setAppliedUniversityId] = useState('');
    const [dateOfApplicationSubmission, setDateOfApplicationSubmission] = useState('');
    const [isConditionalOfferDropdownOpen, setIsConditionalOfferDropdownOpen] = useState(false);
    const [conditionalOffer, setConditionalOffer] = useState('');
    const [conditionalOfferDate, setConditionalOfferDate] = useState('');
    const [dateOfConditionalOfferReceipt, setDateOfConditionalOfferReceipt] = useState('');
    const [uploadedConditionalOfferLetter, setUploadedConditionalOfferLetter] = useState('');
    const [dateOfUnconditionalOfferReceipt, setDateOfUnconditionalOfferReceipt] = useState('');
    const [uploadedUnconditionalOfferLetter, setUploadedUnconditionalOfferLetter] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [isUnconditionalOfferDropdownOpen, setIsUnconditionalOfferDropdownOpen] = useState(false);
    const [unconditionalOffer, setUnconditionalOffer] = useState('');
    const [isFeePaidDropdownOpen, setIsFeePaidDropdownOpen] = useState(false);
    const [feePaid, setFeePaid] = useState('');
    const [feePaidDate, setFeePaidDate] = useState('');
    const [totalFeeAmount, setTotalFeeAmount] = useState('');
    const [firstYearFeeAmount, setFirstYearFeeAmount] = useState('');
    const [isFeePaymentStatusDropdownOpen, setIsFeePaymentStatusDropdownOpen] = useState(false);
    const [feePaymentStatus, setFeePaymentStatus] = useState('');
    const [isFileMovedToVisaDropdownOpen, setIsFileMovedToVisaDropdownOpen] = useState(false);
    const [fileMovedToVisaSearchTerm, setFileMovedToVisaSearchTerm] = useState('');
    const [fileMovedToVisa, setFileMovedToVisa] = useState('');
    const [fileMoveToVisaDate, setFileMoveToVisaDate] = useState('');
    const [dateOfFileMoveToVisaProcessing, setDateOfFileMoveToVisaProcessing] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [admissionStatuses, setAdmissionStatuses] = useState<AdmissionStatus[]>([]);
    const [selectedStatusId, setSelectedStatusId] = useState('');
    const [companyId, setCompanyId] = useState('');
    const [countries, setCountries] = useState<Country[]>([]);
    const [highestPriorityUniversity, setHighestPriorityUniversity] = useState<University | null>(null);
    const [fileSubmissionStatuses, setFileSubmissionStatuses] = useState<FileSubmissionStatus[]>([]);
    const [selectedFileSubmissionStatusId, setSelectedFileSubmissionStatusId] = useState<string | null>(null);
    const [applicationMovedStatuses, setApplicationMovedStatuses] = useState<ApplicationMovedStatus[]>([]);
    const [selectedApplicationMovedStatusId, setSelectedApplicationMovedStatusId] = useState('');
    const [offerLetterStatuses, setOfferLetterStatuses] = useState<OfferLetterStatus[]>([]);
    const [selectedConditionalOfferStatusId, setSelectedConditionalOfferStatusId] = useState('');
    const [selectedUnconditionalOfferStatusId, setSelectedUnconditionalOfferStatusId] = useState('');
    const [feePaymentStatuses, setFeePaymentStatuses] = useState<FeePaymentStatus[]>([]);
    const [selectedFeePaymentStatusId, setSelectedFeePaymentStatusId] = useState('');
    const [visaFileProcessingStatuses, setVisaFileProcessingStatuses] = useState<VisaFileProcessingStatus[]>([]);
    const [selectedVisaFileProcessingStatusId, setSelectedVisaFileProcessingStatusId] = useState('');
    const [welcomeCallByAdmissionTeamStatuses, setWelcomeCallByAdmissionTeamStatuses] = useState<WelcomeCallByAdmissionTeamStatus[]>([]);
    const [selectedWelcomeCallByAdmissionTeamStatusId, setSelectedWelcomeCallByAdmissionTeamStatusId] = useState<string | null>(null);
    const [welcomeCallDate, setWelcomeCallDate] = useState('');
    const [uploadedVisaDocument, setUploadedVisaDocument] = useState('');
    const [studentLanded, setStudentLanded] = useState(false);
    const [airportPickUp, setAirportPickUp] = useState(false);
    const [dateOfTravel, setDateOfTravel] = useState('');
    const dropdownRefs = useRef<DropdownRefs>({});
    const fileInputRef = useRef<HTMLInputElement>(null);
    const unconditionalFileInputRef = useRef<HTMLInputElement>(null);
    const visaDocumentInputRef = useRef<HTMLInputElement>(null);

    const pipelineRefresh = useAppSelector(state => state.pipelineRefresh.refresh);
    const dispatch = useAppDispatch();

    const [collapsibleSections, setCollapsibleSections] = useState({
        basicInfo: false,
        welcomeCall: false,
        applicationStatus: false,
        conditionalOfferLetter: false,
        unconditionalOfferLetter: false,
        feePayment: false,
        visaProcessing: false,
        studentDeparture: false,
    });

    const { leadId: urlLeadId } = useParams();

    const [leadId, setLeadId] = useState(urlLeadId || "");
    const [leadName, setLeadName] = useState("");
    const [leadPhone, setLeadPhone] = useState("");

    useEffect(() => {
        if (leadId) {
            const fetchLeadData = async () => {
                try {
                    const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
                    if (leadDoc.exists()) {
                        const data = leadDoc.data();
                        setLeadName(data.name);
                        setLeadPhone(data.phone);
                    }
                } catch (error) {
                    console.error("Error fetching lead data:", error);
                }
            };
            fetchLeadData();
        }
    }, [leadId]);

    // modal states
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isAdditionalActions, setAdditionalActions] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const currentUser = auth.currentUser;
                if (!currentUser) return;

                let companyId: string | undefined;
                // Get user document to get companyId
                if (currentSubAgent) {
                    companyId = currentSubAgent.companyId;
                } else {
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    companyId = userDoc.data()?.companyId;
                }
                if (!companyId) return;

                setCompanyId(companyId);

                // Fetch admission statuses
                const admissionStatusesSnapshot = await getDocs(collection(db, `companies/${companyId}/leadAdmissionStatus`));
                const admissionStatusesData = admissionStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    admissionStatus: doc.data().admissionStatus
                }));
                setAdmissionStatuses(admissionStatusesData);

                // Fetch file submission statuses
                const fileSubmissionStatusesSnapshot = await getDocs(collection(db, `companies/${companyId}/leadFileSubmissionStatus`));
                const fileSubmissionStatusesData = fileSubmissionStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    fileSubmissionStatus: doc.data().fileSubmissionStatus
                }));
                setFileSubmissionStatuses(fileSubmissionStatusesData);

                // Fetch application moved statuses
                const applicationMovedStatusesSnapshot = await getDocs(collection(db, `companies/${companyId}/leadApplicationMovedStatus`));
                const applicationMovedStatusesData = applicationMovedStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    applicationMovedStatus: doc.data().applicationMovedStatus
                }));
                setApplicationMovedStatuses(applicationMovedStatusesData);

                // Fetch offer letter statuses
                const offerLetterStatusesSnapshot = await getDocs(collection(db, `companies/${companyId}/leadOfferLetterStatus`));
                const offerLetterStatusesData = offerLetterStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    offerLetterStatus: doc.data().offerLetterStatus
                }));
                setOfferLetterStatuses(offerLetterStatusesData);

                // Fetch fee payment statuses
                const feePaymentStatusesSnapshot = await getDocs(collection(db, `companies/${companyId}/leadFeePaymentStatus`));
                const feePaymentStatusesData = feePaymentStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    feePaymentStatus: doc.data().feePaymentStatus
                }));
                setFeePaymentStatuses(feePaymentStatusesData);

                // Fetch visa file processing statuses
                const visaFileProcessingStatusesSnapshot = await getDocs(collection(db, `companies/${companyId}/leadVisaFileProcessingStatus`));
                const visaFileProcessingStatusesData = visaFileProcessingStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    visaFileProcessingStatus: doc.data().visaFileProcessingStatus
                }));
                setVisaFileProcessingStatuses(visaFileProcessingStatusesData);

                // Fetch welcome call by admission team statuses
                const welcomeCallByAdmissionTeamStatusesSnapshot = await getDocs(collection(db, `companies/${companyId}/leadWelcomeCallByAdmissionTeamStatus`));
                const welcomeCallByAdmissionTeamStatusesData = welcomeCallByAdmissionTeamStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    welcomeCallByAdmissionTeamStatus: doc.data().welcomeCallByAdmissionTeamStatus
                }));
                setWelcomeCallByAdmissionTeamStatuses(welcomeCallByAdmissionTeamStatusesData);

                // If leadId exists, fetch current data
                if (leadId) {
                    const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads`, leadId));
                    if (leadDoc.exists()) {
                        const leadData = leadDoc.data();
                        // Set admission status
                        const currentStatusId = leadData.admissionStatusId;
                        if (currentStatusId) {
                            setSelectedStatusId(currentStatusId);
                            const status = admissionStatusesData.find(s => s.id === currentStatusId);
                            if (status) {
                                setSelectedStatus(status.admissionStatus);
                            }
                        }

                        // Set file submission status
                        const currentFileSubmissionStatusId = leadData.fileSubmissionStatusId;
                        if (currentFileSubmissionStatusId) {
                            setSelectedFileSubmissionStatusId(currentFileSubmissionStatusId);
                            const fileSubmissionStatus = fileSubmissionStatusesData.find(s => s.id === currentFileSubmissionStatusId);
                            if (fileSubmissionStatus) {
                                setFileSubmissionStatus(fileSubmissionStatus.fileSubmissionStatus);
                            }
                        }

                        // Set application moved status
                        if (leadData.applicationMovedStatusId) {
                            setSelectedApplicationMovedStatusId(leadData.applicationMovedStatusId);
                        }

                        // Set conditional offer status
                        if (leadData.conditionalOfferLetterStatusId) {
                            setSelectedConditionalOfferStatusId(leadData.conditionalOfferLetterStatusId);
                        }

                        // Set unconditional offer status
                        if (leadData.unconditionalOfferLetterStatusId) {
                            setSelectedUnconditionalOfferStatusId(leadData.unconditionalOfferLetterStatusId);
                        }

                        // Set fee payment status
                        if (leadData.feePaymentStatusId) {
                            setSelectedFeePaymentStatusId(leadData.feePaymentStatusId);
                        }

                        // Set visa file processing status
                        if (leadData.visaFileProcessingStatusId) {
                            setSelectedVisaFileProcessingStatusId(leadData.visaFileProcessingStatusId);
                        }

                        // Set welcome call by admission team status
                        if (leadData.welcomeCallByAdmissionTeamStatusId) {
                            setSelectedWelcomeCallByAdmissionTeamStatusId(leadData.welcomeCallByAdmissionTeamStatusId);
                        }

                        // Set other fields
                        setAgencyName(leadData.agencyName || '');
                        setProgramName(leadData.programName || '');
                        setProgramRemarks(leadData.programRemarks || '');
                        setSelectedCountryName(leadData.selectedCountryName || '');
                        setWelcomeCallDate(leadData.welcomeCallDate || '');
                        setFinalRemarks(leadData.finalRemarks || '');
                        setUploadedConditionalOfferLetter(leadData.uploadedConditionalOfferLetter || '');
                        setUploadedUnconditionalOfferLetter(leadData.uploadedUnconditionalOfferLetter || '');
                        setUploadedVisaDocument(leadData.uploadedVisaDocument || '');
                        setStudentLanded(leadData.studentLanded || false);
                        setAirportPickUp(leadData.airportPickUp || false);

                        // Date of Travel timestamp conversion
                        if (leadData.dateOfTravel) {
                            const date = leadData.dateOfTravel.toDate();
                            setDateOfTravel(date.toISOString().split('T')[0]);
                        }
                        setTotalFeeAmount(leadData.totalFeeAmount?.toString() || '');
                        setFirstYearFeeAmount(leadData.firstYearFeeAmount?.toString() || '');
                        setAppliedUniversityId(leadData.appliedUniversityId || '');

                        // Convert Timestamps to local date strings if they exist
                        if (leadData.welcomeCallDate) {
                            const date = leadData.welcomeCallDate.toDate();
                            setWelcomeCallDate(date.toISOString().split('T')[0]);
                        }
                        if (leadData.dateOfApplicationSubmission) {
                            const date = leadData.dateOfApplicationSubmission.toDate();
                            setDateOfApplicationSubmission(date.toISOString().split('T')[0]);
                        }
                        if (leadData.dateOfConditionalOfferReceipt) {
                            const date = leadData.dateOfConditionalOfferReceipt.toDate();
                            setDateOfConditionalOfferReceipt(date.toISOString().split('T')[0]);
                        }
                        if (leadData.dateOfUnconditionalOfferReceipt) {
                            const date = leadData.dateOfUnconditionalOfferReceipt.toDate();
                            setDateOfUnconditionalOfferReceipt(date.toISOString().split('T')[0]);
                        }
                        if (leadData.dateOfFileMoveToVisaProcessing) {
                            const date = leadData.dateOfFileMoveToVisaProcessing.toDate();
                            setDateOfFileMoveToVisaProcessing(date.toISOString().split('T')[0]);
                        }

                        // Set selected country
                        if (leadData.countryId) {
                            setSelectedCountry(leadData.countryId);
                            const countryDoc = await getDoc(doc(db, 'countries', leadData.countryId));
                            if (countryDoc.exists()) {
                                setSelectedCountryName(countryDoc.data().name);
                            }
                        }

                        // Fetch highest priority university
                        if (leadData.appliedUniversityId) {
                            const uniDoc = await getDoc(doc(db, 'universities', leadData.appliedUniversityId));
                            if (uniDoc.exists()) {
                                setHighestPriorityUniversity({
                                    id: uniDoc.id,
                                    name: uniDoc.data().name,
                                    uniCode: uniDoc.data().uniCode
                                });
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setToastMessage('Error fetching data');
                setIsErrorToast(true);
                setShowToast(true);
            }
        };

        fetchData();
    }, [leadId]);

    const handleStatusSelect = (status: AdmissionStatus) => {
        setSelectedStatus(status.admissionStatus);
        setSelectedStatusId(status.id);
        setIsStatusDropdownOpen(false);
    };

    const confirmSave = () => {
        // Open confirmation modal
        setIsConfirmModalOpen(true);
    }

    const handleSave = async () => {
        try {
            setIsSaving(true);

            if (leadId && companyId) {
                // Helper function to convert empty strings to null
                const sanitizeValue = (value: any) => {
                    if (typeof value === 'string' && value.trim() === '') {
                        return null;
                    }
                    return value;
                };

                const updateData: any = {
                    updatedAt: serverTimestamp(),
                    updatedBy: auth.currentUser?.uid,
                };

                // Add status IDs with sanitization
                const statusFields = {
                    admissionStatusId: selectedStatusId,
                    fileSubmissionStatusId: selectedFileSubmissionStatusId,
                    applicationMovedStatusId: selectedApplicationMovedStatusId,
                    conditionalOfferLetterStatusId: selectedConditionalOfferStatusId,
                    unconditionalOfferLetterStatusId: selectedUnconditionalOfferStatusId,
                    feePaymentStatusId: selectedFeePaymentStatusId,
                    visaFileProcessingStatusId: selectedVisaFileProcessingStatusId,
                    welcomeCallByAdmissionTeamStatusId: selectedWelcomeCallByAdmissionTeamStatusId,
                    countryId: selectedCountry
                };

                // Only add status fields that have values
                Object.entries(statusFields).forEach(([key, value]) => {
                    if (value) {
                        updateData[key] = value;
                    }
                });

                // Add text fields with sanitization
                const textFields = {
                    agencyName: agencyName,
                    programName: programName,
                    programRemarks: programRemarks,
                    finalRemarks: finalRemarks
                };

                Object.entries(textFields).forEach(([key, value]) => {
                    const sanitizedValue = sanitizeValue(value);
                    if (sanitizedValue !== null && sanitizedValue !== undefined) {
                        updateData[key] = sanitizedValue;
                    }
                });

                // Handle date fields
                const dateFields = {
                    welcomeCallDate: welcomeCallDate,
                    dateOfApplicationSubmission: dateOfApplicationSubmission,
                    dateOfConditionalOfferReceipt: dateOfConditionalOfferReceipt,
                    dateOfUnconditionalOfferReceipt: dateOfUnconditionalOfferReceipt,
                    dateOfFileMoveToVisaProcessing: dateOfFileMoveToVisaProcessing,
                    dateOfTravel: dateOfTravel
                };

                Object.entries(dateFields).forEach(([key, value]) => {
                    if (value) {
                        const [year, month, day] = value.split('-').map(Number);
                        // Create date with time component set to noon IST to avoid timezone issues
                        const dateInIST = new Date(year, month - 1, day, 12);
                        updateData[key] = Timestamp.fromDate(dateInIST);
                    }
                });

                // Add boolean fields
                updateData.studentLanded = studentLanded;
                updateData.airportPickUp = airportPickUp;

                // Handle numeric fields
                if (totalFeeAmount) {
                    updateData.totalFeeAmount = parseFloat(totalFeeAmount);
                }
                if (firstYearFeeAmount) {
                    updateData.firstYearFeeAmount = parseFloat(firstYearFeeAmount);
                }

                // Update the lead document
                await updateDoc(doc(db, `companies/${companyId}/leads`, leadId), updateData);

                setToastMessage('Details saved successfully');
                setIsErrorToast(false);
                setShowToast(true);
                // Refresh the pipeline view if needed
                dispatch(setPipelineRefresh(true));
            }
        } catch (error) {
            console.error('Error saving details:', error);
            setToastMessage('Error saving details');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsSaving(false);
            setIsConfirmModalOpen(false);
            setAdditionalActions(true);
        }
    };

    const handleWelcomeCallByAdmissionTeamSelect = (status: WelcomeCallByAdmissionTeamStatus) => {
        setSelectedWelcomeCallByAdmissionTeamStatusId(status.id);
        setWelcomeCallSearchTerm('');
        setIsWelcomeCallByAdmissionTeamDropdownOpen(false);
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'conditional' | 'unconditional' | 'visa') => {
        const file = event.target.files?.[0];
        if (!file || !leadId || !companyId) return;

        // Check file type
        if (!['image/jpeg', 'image/jpg', 'application/pdf'].includes(file.type)) {
            setToastMessage('Please upload only JPG, JPEG or PDF files');
            setIsErrorToast(true);
            setShowToast(true);
            return;
        }

        try {
            setIsUploading(true);
            const fileName = `${Date.now()}_${file.name}`;
            const storageRef = ref(storage, `student_documents/${companyId}/${leadId}/${fileName}`);

            // Upload file
            await uploadBytes(storageRef, file);

            // Determine field name based on type
            let fieldName = '';
            if (type === 'conditional') {
                fieldName = 'uploadedConditionalOfferLetter';
            } else if (type === 'unconditional') {
                fieldName = 'uploadedUnconditionalOfferLetter';
            } else if (type === 'visa') {
                fieldName = 'uploadedVisaDocument';
            }

            // Update lead document
            await updateDoc(doc(db, `companies/${companyId}/leads`, leadId), {
                [fieldName]: fileName,
                updatedAt: new Date(),
                updatedBy: auth.currentUser?.uid
            });

            if (type === 'conditional') {
                setUploadedConditionalOfferLetter(fileName);
            } else if (type === 'unconditional') {
                setUploadedUnconditionalOfferLetter(fileName);
            } else if (type === 'visa') {
                setUploadedVisaDocument(fileName);
            }
            setToastMessage('File uploaded successfully');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            console.error('Error uploading file:', error);
            setToastMessage('Error uploading file');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileDelete = async (type: 'conditional' | 'unconditional' | 'visa') => {
        const fileName = type === 'conditional' ?
            uploadedConditionalOfferLetter :
            type === 'unconditional' ?
                uploadedUnconditionalOfferLetter :
                uploadedVisaDocument;

        if (!fileName || !leadId || !companyId) return;

        try {
            setIsUploading(true);

            // Delete from storage
            const storageRef = ref(storage, `student_documents/${companyId}/${leadId}/${fileName}`);
            await deleteObject(storageRef);

            // Determine field name based on type
            let fieldName = '';
            if (type === 'conditional') {
                fieldName = 'uploadedConditionalOfferLetter';
            } else if (type === 'unconditional') {
                fieldName = 'uploadedUnconditionalOfferLetter';
            } else if (type === 'visa') {
                fieldName = 'uploadedVisaDocument';
            }

            // Update lead document
            await updateDoc(doc(db, `companies/${companyId}/leads`, leadId), {
                [fieldName]: deleteField(),
                updatedAt: new Date(),
                updatedBy: auth.currentUser?.uid
            });

            if (type === 'conditional') {
                setUploadedConditionalOfferLetter('');
            } else if (type === 'unconditional') {
                setUploadedUnconditionalOfferLetter('');
            } else if (type === 'visa') {
                setUploadedVisaDocument('');
            }
            setToastMessage('File deleted successfully');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            console.error('Error deleting file:', error);
            setToastMessage('Error deleting file');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileDownload = async (type: 'conditional' | 'unconditional' | 'visa') => {
        const fileName = type === 'conditional' ?
            uploadedConditionalOfferLetter :
            type === 'unconditional' ?
                uploadedUnconditionalOfferLetter :
                uploadedVisaDocument;

        if (!fileName || !leadId || !companyId) return;

        try {
            // For visa documents, we don't need the university ID as it applies to the whole lead
            const storagePath = type === 'visa'
                ? `student_documents/${companyId}/${leadId}/${fileName}`
                : `student_documents/${companyId}/${leadId}/universities/${appliedUniversityId}/${fileName}`;

            const storageRef = ref(storage, storagePath);
            const url = await getDownloadURL(storageRef);
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error downloading file:', error);
            setToastMessage('Error downloading file');
            setIsErrorToast(true);
            setShowToast(true);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            const dropdownSetters: DropdownSetters = {
                status: setIsStatusDropdownOpen,
                welcomeCallByAdmissionTeam: setIsWelcomeCallByAdmissionTeamDropdownOpen,
                fileSubmissionStatus: setIsFileSubmissionDropdownOpen,
                applicationMoved: setIsApplicationMovedDropdownOpen,
                conditionalOffer: setIsConditionalOfferDropdownOpen,
                unconditionalOffer: setIsUnconditionalOfferDropdownOpen,
                feePaymentStatus: setIsFeePaymentStatusDropdownOpen,
                fileMovedToVisa: setIsFileMovedToVisaDropdownOpen,
                country: setIsCountryDropdownOpen,

            };

            Object.entries(dropdownSetters).forEach(([key, setter]) => {
                const ref = dropdownRefs.current[key];
                if (ref && !ref.contains(target)) {
                    setter(false);
                }
            });


        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const inputClass = `w-full py-2 pl-4 pr-4 bg-white border border-cyan-600 rounded-3xl focus:outline-none focus:ring-2 focus:ring-sky-950 ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`;
    const labelClass = "block font-montserrat font-normal text-base text-black mb-2";
    const sectionClass = "group mb-8 bg-white rounded-lg shadow-sm transition-all duration-300 border border-gray-100";
    const sectionHeaderClass = "text-lg font-semibold text-sky-950 ";

    return (
        <div>
            <div className="mt-8 mx-10 space-y-6">
                {/* Basic Information Section */}
                <div className={`${sectionClass} ${collapsibleSections.basicInfo ? 'p-6 border-b-2 ring-1 ring-cyan-500' : 'p-0'}`}>
                    <div className='p-5 cursor-pointer' onClick={() => setCollapsibleSections({ ...collapsibleSections, basicInfo: !collapsibleSections.basicInfo })}>
                        <h2 className={`${sectionHeaderClass} ${collapsibleSections.basicInfo ? 'mb-4 pb-2 border-b border-gray-200' : 'mb-2 pb-0 border-0'}`}>Basic Information</h2>
                    </div>
                    {collapsibleSections.basicInfo && (
                        <>
                            <div className="grid grid-cols-3 gap-6">
                                <div>
                                    <label htmlFor="status" className={labelClass}>
                                        Admission Status
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            id="status"
                                            value={selectedStatus}
                                            placeholder="Auto Populated From Applied Universities Tab"
                                            className={`${inputClass} bg-gray-50 cursor-not-allowed text-gray-500 font-semibold`}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelClass}>Country</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={selectedCountryName}
                                            className={`${inputClass} bg-gray-50 cursor-not-allowed text-gray-500 font-semibold`}
                                            readOnly
                                            disabled
                                            placeholder="Auto Populated From Applied Universities Tab"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelClass}>University Applied to</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={highestPriorityUniversity?.name}
                                            className={`${inputClass} bg-gray-50 cursor-not-allowed text-gray-500 font-semibold`}
                                            placeholder="Auto Populated From Applied Universities Tab"
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                </div>

                            </div>

                            <div className="grid grid-cols-3 gap-6 mt-6">
                                <div>
                                    <label className={labelClass}>Agent/Agency Name</label>
                                    <input
                                        type="text"
                                        value={agencyName}
                                        placeholder="Auto Populated From Applied Universities Tab"
                                        className={`${inputClass} bg-gray-50 cursor-not-allowed text-gray-500 font-semibold`}
                                        readOnly
                                        disabled
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Program Name</label>
                                    <input
                                        type="text"
                                        value={programName}
                                        placeholder="Auto Populated From Applied Universities Tab"
                                        className={`${inputClass} bg-gray-50 cursor-not-allowed text-gray-500 font-semibold`}
                                        readOnly
                                        disabled
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Program Remarks (if any)</label>
                                    <textarea
                                        value={programRemarks}
                                        placeholder="Auto Populated From Applied Universities Tab"
                                        className={`${inputClass} bg-gray-50 cursor-not-allowed text-gray-500 font-semibold`}
                                        readOnly
                                        disabled
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Welcome Call & File Submission Section */}
                <div className={`${sectionClass} ${collapsibleSections.welcomeCall ? 'p-6 border-b-2 ring-1 ring-cyan-500' : 'p-0'}`}>
                    <div className='p-5 cursor-pointer' onClick={() => setCollapsibleSections({ ...collapsibleSections, welcomeCall: !collapsibleSections.welcomeCall })}>
                        <h2 className={`${sectionHeaderClass} ${collapsibleSections.welcomeCall ? 'mb-4 pb-2 border-b border-gray-200' : 'mb-2 pb-0 border-0'}`}>Welcome Call & File Submission</h2>
                    </div>
                    {collapsibleSections.welcomeCall && (
                        <div className="grid grid-cols-3 gap-6">
                            <div className="relative" ref={el => { if (el) dropdownRefs.current.welcomeCallByAdmissionTeam = el; }}>
                                <label className={labelClass}>Welcome Call by Admission Team</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={isWelcomeCallByAdmissionTeamDropdownOpen ? welcomeCallSearchTerm : welcomeCallByAdmissionTeamStatuses.find(status => status.id === selectedWelcomeCallByAdmissionTeamStatusId)?.welcomeCallByAdmissionTeamStatus || ''}
                                        placeholder="Select Welcome Call Status"
                                        onClick={() => {
                                            const newState = !isWelcomeCallByAdmissionTeamDropdownOpen;
                                            setIsWelcomeCallByAdmissionTeamDropdownOpen(newState);
                                            if (newState) setWelcomeCallSearchTerm('');
                                        }}
                                        onChange={(e) => setWelcomeCallSearchTerm(e.target.value)}
                                        className={inputClass}
                                        disabled={!write}
                                        readOnly={!write}
                                    />
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                                        <button
                                            type="button"
                                            className={`${!write ? 'cursor-not-allowed' : ''}`}
                                            disabled={!write}
                                            onClick={() => {
                                                const newState = !isWelcomeCallByAdmissionTeamDropdownOpen;
                                                setIsWelcomeCallByAdmissionTeamDropdownOpen(newState);
                                                if (newState) setWelcomeCallSearchTerm('');
                                            }}
                                        >
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                                alt="Dropdown Arrow"
                                                className={`w-7 h-7 transition-transform ${isWelcomeCallByAdmissionTeamDropdownOpen ? 'rotate-180' : ''}`}
                                            />
                                        </button>
                                        {selectedWelcomeCallByAdmissionTeamStatusId && (
                                            <button
                                                type="button"
                                                className={`${!write ? 'cursor-not-allowed' : ''}`}
                                                disabled={!write}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedWelcomeCallByAdmissionTeamStatusId(null);
                                                    setWelcomeCallSearchTerm('');
                                                }}
                                            >
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`}
                                                    alt="Clear"
                                                    className="w-5 h-5"
                                                />
                                            </button>
                                        )}
                                    </div>
                                    {isWelcomeCallByAdmissionTeamDropdownOpen && (
                                        <div className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-10 max-h-[300px] overflow-hidden">
                                            <div className="p-2">
                                                <div className="space-y-1 max-h-[280px] overflow-y-auto">
                                                    {welcomeCallByAdmissionTeamStatuses
                                                        .filter(status =>
                                                            status.welcomeCallByAdmissionTeamStatus.toLowerCase().includes(welcomeCallSearchTerm.toLowerCase())
                                                        )
                                                        .map((status) => (
                                                            <button
                                                                key={status.id}
                                                                onClick={() => handleWelcomeCallByAdmissionTeamSelect(status)}
                                                                className="w-full py-2 px-4 text-left text-gray-800 hover:bg-gray-100 rounded-xl"
                                                            >
                                                                {status.welcomeCallByAdmissionTeamStatus}
                                                            </button>
                                                        ))}
                                                    {welcomeCallSearchTerm && !welcomeCallByAdmissionTeamStatuses.some(status =>
                                                        status.welcomeCallByAdmissionTeamStatus.toLowerCase().includes(welcomeCallSearchTerm.toLowerCase())
                                                    ) && (
                                                            <div className="px-4 py-2 text-gray-500">No matching status found</div>
                                                        )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>Date of Welcome Call</label>
                                <input
                                    type="date"
                                    value={welcomeCallDate}
                                    onChange={(e) => setWelcomeCallDate(e.target.value)}
                                    className={inputClass}
                                    disabled={!write}
                                    readOnly={!write}
                                />
                            </div>
                            <div className="relative" ref={el => { if (el) dropdownRefs.current.fileSubmissionStatus = el; }}>
                                <label className={labelClass}>File Submission Status</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={isFileSubmissionDropdownOpen ? fileSubmissionSearchTerm : fileSubmissionStatuses.find(status => status.id === selectedFileSubmissionStatusId)?.fileSubmissionStatus || ''}
                                        placeholder="Select File Submission Status"
                                        onClick={() => {
                                            const newState = !isFileSubmissionDropdownOpen;
                                            setIsFileSubmissionDropdownOpen(newState);
                                            if (newState) setFileSubmissionSearchTerm('');
                                        }}
                                        onChange={(e) => setFileSubmissionSearchTerm(e.target.value)}
                                        className={inputClass}
                                        disabled={!write}
                                        readOnly={!write}
                                    />
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                                        <button
                                            type="button"
                                            className={`${!write ? 'cursor-not-allowed' : ''}`}
                                            disabled={!write}
                                            onClick={() => {
                                                const newState = !isFileSubmissionDropdownOpen;
                                                setIsFileSubmissionDropdownOpen(newState);
                                                if (newState) setFileSubmissionSearchTerm('');
                                            }}
                                        >
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                                alt="Dropdown Arrow"
                                                className={`w-7 h-7 transition-transform ${isFileSubmissionDropdownOpen ? 'rotate-180' : ''}`}
                                            />
                                        </button>
                                        {selectedFileSubmissionStatusId && (
                                            <button
                                                type="button"
                                                className={`${!write ? 'cursor-not-allowed' : ''}`}
                                                disabled={!write}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedFileSubmissionStatusId(null);
                                                    setFileSubmissionSearchTerm('');
                                                }}
                                            >
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`}
                                                    alt="Clear"
                                                    className="w-5 h-5"
                                                />
                                            </button>
                                        )}
                                    </div>
                                    {isFileSubmissionDropdownOpen && (
                                        <div className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-10 max-h-[300px] overflow-hidden">
                                            <div className="p-2">
                                                <div className="space-y-1 max-h-[280px] overflow-y-auto">
                                                    {fileSubmissionStatuses
                                                        .filter(status =>
                                                            status.fileSubmissionStatus.toLowerCase().includes(fileSubmissionSearchTerm.toLowerCase())
                                                        )
                                                        .map((status) => (
                                                            <button
                                                                key={status.id}
                                                                onClick={() => {
                                                                    setSelectedFileSubmissionStatusId(status.id);
                                                                    setFileSubmissionSearchTerm('');
                                                                    setIsFileSubmissionDropdownOpen(false);
                                                                }}
                                                                className="w-full py-2 px-4 text-left text-gray-800 hover:bg-gray-100 rounded-xl"
                                                            >
                                                                {status.fileSubmissionStatus}
                                                            </button>
                                                        ))}
                                                    {fileSubmissionSearchTerm && !fileSubmissionStatuses.some(status =>
                                                        status.fileSubmissionStatus.toLowerCase().includes(fileSubmissionSearchTerm.toLowerCase())
                                                    ) && (
                                                            <div className="px-4 py-2 text-gray-500">No matching status found</div>
                                                        )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Application Status Section */}
                <div className={`${sectionClass} ${collapsibleSections.applicationStatus ? 'p-6 border-b-2 ring-1 ring-cyan-500' : 'p-0'}`}>
                    <div className='p-5 cursor-pointer' onClick={() => setCollapsibleSections({ ...collapsibleSections, applicationStatus: !collapsibleSections.applicationStatus })}>
                        <h2 className={`${sectionHeaderClass} ${collapsibleSections.applicationStatus ? 'mb-4 pb-2 border-b border-gray-200' : 'mb-2 pb-0 border-0'}`}>Application Status</h2>
                    </div>
                    {collapsibleSections.applicationStatus && (
                        <div className="grid grid-cols-3 gap-6">
                            <div className="relative" ref={el => { if (el) dropdownRefs.current.applicationMoved = el; }}>
                                <label className={labelClass}>Application Moved</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={applicationMovedStatuses.find(status => status.id === selectedApplicationMovedStatusId)?.applicationMovedStatus || ''}
                                        placeholder="Auto Populated From Applied Universities Tab"
                                        className={`${inputClass} bg-gray-50 cursor-not-allowed text-gray-500 font-semibold`}
                                        readOnly
                                    />
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>Final Remarks</label>
                                <input
                                    type="text"
                                    value={finalRemarks}
                                    placeholder="Auto Populated From Applied Universities Tab"
                                    className={`${inputClass} bg-gray-50 cursor-not-allowed text-gray-500 font-semibold`}
                                    readOnly
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Date of University/ College Application Submission</label>
                                <input
                                    type="date"
                                    value={dateOfApplicationSubmission}
                                    className={`${inputClass} bg-gray-50 cursor-not-allowed`}
                                    readOnly
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Conditional Offer Section */}
                <div className={`${sectionClass} ${collapsibleSections.conditionalOfferLetter ? 'p-6 border-b-2 ring-1 ring-cyan-500' : 'p-0'}`}>
                    <div className='p-5 cursor-pointer' onClick={() => setCollapsibleSections({ ...collapsibleSections, conditionalOfferLetter: !collapsibleSections.conditionalOfferLetter })}>
                        <h2 className={`${sectionHeaderClass} ${collapsibleSections.conditionalOfferLetter ? 'mb-4 pb-2 border-b border-gray-200' : 'mb-2 pb-0 border-0'}`}>Conditional Offer Details</h2>
                    </div>
                    {collapsibleSections.conditionalOfferLetter && (
                        <div className="grid grid-cols-3 gap-6">
                            <div className="relative" ref={el => { if (el) dropdownRefs.current.conditionalOffer = el; }}>
                                <label className={labelClass}>Conditional Offer Received?</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={offerLetterStatuses.find(status => status.id === selectedConditionalOfferStatusId)?.offerLetterStatus || ''}
                                        placeholder="Auto Populated From Applied Universities Tab"
                                        className={`${inputClass} bg-gray-50 cursor-not-allowed text-gray-500 font-semibold`}
                                        readOnly
                                    />
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>Date of Receipt</label>
                                <input
                                    type="date"
                                    value={dateOfConditionalOfferReceipt}
                                    className={`${inputClass} bg-gray-50 cursor-not-allowed`}
                                    readOnly
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Upload Conditional Offer Letter</label>
                                <div
                                    className={`relative ${uploadedConditionalOfferLetter ? 'cursor-pointer' : ''} ${inputClass}`}
                                    onClick={() => uploadedConditionalOfferLetter ? handleFileDownload('conditional') : ''}
                                >
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept=".jpg,.jpeg,.pdf"
                                        className="hidden"
                                    />
                                    <div className="flex items-center justify-between">
                                        <span className={uploadedConditionalOfferLetter ? 'text-blue-600' : 'text-gray-400 font-semibold'}>
                                            {uploadedConditionalOfferLetter || 'Auto Populated From Applied Universities Tab'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Unconditional Offer Section */}
                <div className={`${sectionClass} ${collapsibleSections.unconditionalOfferLetter ? 'p-6 border-b-2 ring-1 ring-cyan-500' : 'p-0'}`}>
                    <div className='p-5 cursor-pointer' onClick={() => setCollapsibleSections({ ...collapsibleSections, unconditionalOfferLetter: !collapsibleSections.unconditionalOfferLetter })}>
                        <h2 className={`${sectionHeaderClass} ${collapsibleSections.unconditionalOfferLetter ? 'mb-4 pb-2 border-b border-gray-200' : 'mb-2 pb-0 border-0'}`}>Unconditional Offer Details</h2>
                    </div>
                    {collapsibleSections.unconditionalOfferLetter && (
                        <div className="grid grid-cols-3 gap-6">
                            <div className="relative" ref={el => { if (el) dropdownRefs.current.unconditionalOffer = el; }}>
                                <label className={labelClass}>Unconditional Offer Received?</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={offerLetterStatuses.find(status => status.id === selectedUnconditionalOfferStatusId)?.offerLetterStatus || ''}
                                        placeholder="Auto Populated From Applied Universities Tab"
                                        className={`${inputClass} bg-gray-50 cursor-not-allowed text-gray-500 font-semibold`}
                                        readOnly
                                    />
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>Date of Receipt</label>
                                <input
                                    type="date"
                                    value={dateOfUnconditionalOfferReceipt}
                                    className={`${inputClass} bg-gray-50 cursor-not-allowed`}
                                    readOnly
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Upload Unconditional Offer Letter</label>
                                <div
                                    className={`relative ${uploadedUnconditionalOfferLetter ? 'cursor-pointer' : ''} ${inputClass}`}
                                    onClick={() => uploadedUnconditionalOfferLetter ? handleFileDownload('unconditional') : ''}
                                >
                                    <input
                                        type="file"
                                        ref={unconditionalFileInputRef}
                                        accept=".jpg,.jpeg,.pdf"
                                        className="hidden"
                                    />
                                    <div className="flex items-center justify-between">
                                        <span className={uploadedUnconditionalOfferLetter ? 'text-blue-600' : 'text-gray-400 font-semibold'}>
                                            {uploadedUnconditionalOfferLetter || 'Auto Populated From Applied Universities Tab'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Fee Payment Section */}
                <div className={`${sectionClass} ${collapsibleSections.feePayment ? 'p-6 border-b-2 ring-1 ring-cyan-500' : 'p-0'}`}>
                    <div className='p-5 cursor-pointer' onClick={() => setCollapsibleSections({ ...collapsibleSections, feePayment: !collapsibleSections.feePayment })}>
                        <h2 className={`${sectionHeaderClass} ${collapsibleSections.feePayment ? 'mb-4 pb-2 border-b border-gray-200' : 'mb-2 pb-0 border-0'}`}>Fee Payment Details</h2>
                    </div>
                    {collapsibleSections.feePayment && (
                        <div className="grid grid-cols-3 gap-6">
                            <div>
                                <label className={labelClass}>Total Fee Amount</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={totalFeeAmount}
                                    className={`${inputClass} bg-gray-50 cursor-not-allowed text-gray-500 font-semibold`}
                                    placeholder="Auto Populated From Applied Universities Tab"
                                    disabled
                                    readOnly
                                />
                            </div>

                            <div>
                                <label className={labelClass}>First Year Fee Amount</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={firstYearFeeAmount}
                                    className={`${inputClass} bg-gray-50 cursor-not-allowed text-gray-500 font-semibold`}
                                    placeholder="Auto Populated From Applied Universities Tab"
                                    disabled
                                    readOnly
                                />
                            </div>

                            <div className="relative" ref={el => { if (el) dropdownRefs.current.feePaymentStatus = el; }}>
                                <label className={labelClass}>Fee Payment Status</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={feePaymentStatuses.find(status => status.id === selectedFeePaymentStatusId)?.feePaymentStatus || ''}
                                        placeholder="Auto Populated From Applied Universities Tab"
                                        disabled
                                        className={`${inputClass} bg-gray-50 cursor-not-allowed text-gray-500 font-semibold`}
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Visa Processing Section */}
                <div className={`${sectionClass} ${collapsibleSections.visaProcessing ? 'p-6 border-b-2 ring-1 ring-cyan-500' : 'p-0'}`}>
                    <div className='p-5 cursor-pointer' onClick={() => setCollapsibleSections({ ...collapsibleSections, visaProcessing: !collapsibleSections.visaProcessing })}>
                        <h2 className={`${sectionHeaderClass} ${collapsibleSections.visaProcessing ? 'mb-4 pb-2 border-b border-gray-200' : 'mb-2 pb-0 border-0'}`}>Visa Processing Details</h2>
                    </div>
                    {collapsibleSections.visaProcessing && (
                        <div className="grid grid-cols-3 gap-6">
                            <div className="relative" ref={el => { if (el) dropdownRefs.current.fileMovedToVisa = el; }}>
                                <label className={labelClass}>File moved to Visa Processing?</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={isFileMovedToVisaDropdownOpen ? fileMovedToVisaSearchTerm : visaFileProcessingStatuses.find(status => status.id === selectedVisaFileProcessingStatusId)?.visaFileProcessingStatus || ''}
                                        placeholder="Select Visa File Processing Status"
                                        onClick={() => {
                                            const newState = !isFileMovedToVisaDropdownOpen;
                                            setIsFileMovedToVisaDropdownOpen(newState);
                                            if (newState) setFileMovedToVisaSearchTerm('');
                                        }}
                                        onChange={(e) => setFileMovedToVisaSearchTerm(e.target.value)}
                                        className={inputClass}
                                        disabled={!write}
                                        readOnly={!write}
                                    />
                                    <button
                                        type="button"
                                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${!write ? 'cursor-not-allowed' : ''}`}
                                        disabled={!write}
                                        onClick={() => {
                                            const newState = !isFileMovedToVisaDropdownOpen;
                                            setIsFileMovedToVisaDropdownOpen(newState);
                                            if (newState) setFileMovedToVisaSearchTerm('');
                                        }}
                                    >
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                            alt="Dropdown Arrow"
                                            className={`w-7 h-7 transition-transform ${isFileMovedToVisaDropdownOpen ? 'rotate-180' : ''}`}
                                        />
                                    </button>
                                    {isFileMovedToVisaDropdownOpen && (
                                        <div className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-10 max-h-[300px] overflow-hidden">
                                            <div className="p-2">
                                                <div className="space-y-1 max-h-[280px] overflow-y-auto">
                                                    {visaFileProcessingStatuses
                                                        .filter(status =>
                                                            status.visaFileProcessingStatus.toLowerCase().includes(fileMovedToVisaSearchTerm.toLowerCase())
                                                        )
                                                        .map((status) => (
                                                            <button
                                                                key={status.id}
                                                                onClick={() => {
                                                                    setSelectedVisaFileProcessingStatusId(status.id);
                                                                    setFileMovedToVisaSearchTerm('');
                                                                    setIsFileMovedToVisaDropdownOpen(false);
                                                                }}
                                                                className="w-full py-2 px-4 text-left text-gray-800 hover:bg-gray-100 rounded-xl"
                                                            >
                                                                {status.visaFileProcessingStatus}
                                                            </button>
                                                        ))}
                                                    {fileMovedToVisaSearchTerm && !visaFileProcessingStatuses.some(status =>
                                                        status.visaFileProcessingStatus.toLowerCase().includes(fileMovedToVisaSearchTerm.toLowerCase())
                                                    ) && (
                                                            <div className="px-4 py-2 text-gray-500">No matching status found</div>
                                                        )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Date of File moved</label>
                                <input
                                    type="date"
                                    value={dateOfFileMoveToVisaProcessing}
                                    onChange={(e) => setDateOfFileMoveToVisaProcessing(e.target.value)}
                                    className={inputClass}
                                    disabled={!write}
                                    readOnly={!write}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Upload VISA Document</label>
                                <div
                                    onClick={() => {
                                        if (uploadedVisaDocument) {
                                            handleFileDownload('visa');
                                        } else {
                                            document.getElementById('visa-document-upload')?.click();
                                        }
                                    }}
                                    className={`border rounded-full p-3 relative ${uploadedVisaDocument ? 'hover:bg-cyan-50/50' : 'hover:bg-gray-50'} ${inputClass}`}
                                >
                                    <input
                                        type="file"
                                        accept=".jpg,.jpeg,.pdf"
                                        onChange={(e) => handleFileUpload(e, 'visa')}
                                        className={`hidden ${!write ? 'cursor-not-allowed' : ''}`}
                                        readOnly={!write}
                                        disabled={!write}
                                        id="visa-document-upload"
                                    />
                                    <div className="flex items-center justify-between">
                                        <span className={uploadedVisaDocument ? 'text-cyan-600' : 'text-gray-400 font-semibold'}>
                                            {uploadedVisaDocument || 'Upload Document (JPG, JPEG, PDF)'}
                                        </span>
                                        {uploadedVisaDocument && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleFileDelete('visa');
                                                }}
                                                className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    {isUploading && (
                                        <div className="absolute inset-0 bg-gray-100 bg-opacity-50 flex items-center justify-center rounded-full">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                        </div>
                                    )}
                                </div>
                            </div>


                        </div>
                    )}
                </div>

                {/* Student Departure Details Section */}
                <div className={`${sectionClass} ${collapsibleSections.studentDeparture ? 'p-6 border-b-2 ring-1 ring-cyan-500' : 'p-0'}`}>
                    <div className='p-5 cursor-pointer' onClick={() => setCollapsibleSections({ ...collapsibleSections, studentDeparture: !collapsibleSections.studentDeparture })}>
                        <h2 className={`${sectionHeaderClass} ${collapsibleSections.studentDeparture ? 'mb-4 pb-2 border-b border-gray-200' : 'mb-2 pb-0 border-0'}`}>Student Departure Details</h2>
                    </div>
                    {collapsibleSections.studentDeparture && (
                        <div className="grid grid-cols-3 gap-6">
                            <div>
                                <label className={labelClass}>Student Landed</label>
                                <div className="relative">
                                    <select
                                        value={studentLanded ? "Yes" : "No"}
                                        onChange={(e) => setStudentLanded(e.target.value === "Yes")}
                                        className={inputClass}
                                        disabled={!write}
                                    >
                                        <option value="No">No</option>
                                        <option value="Yes">Yes</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Airport Pick Up</label>
                                <div className="relative">
                                    <select
                                        value={airportPickUp ? "Yes" : "No"}
                                        onChange={(e) => setAirportPickUp(e.target.value === "Yes")}
                                        className={inputClass}
                                        disabled={!write}
                                    >
                                        <option value="No">No</option>
                                        <option value="Yes">Yes</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Date of Travel</label>
                                <input
                                    type="date"
                                    value={dateOfTravel}
                                    onChange={(e) => setDateOfTravel(e.target.value)}
                                    className={inputClass}
                                    disabled={!write}
                                    readOnly={!write}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Save Button Section */}
                {write && (
                    <div className="flex items-center justify-center py-6">
                        <button
                            onClick={confirmSave}
                            disabled={isSaving}
                            className={`w-32 text-center p-3 text-white bg-gradient-to-r from-[#3483BE] to-[#02385E] rounded-3xl font-medium hover:bg-gray-50 relative ${isSaving ? 'cursor-not-allowed opacity-75' : ''
                                }`}
                        >
                            <span className={isSaving ? 'invisible' : ''}>Save</span>
                            {isSaving && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                </div>
                            )}
                        </button>
                    </div>
                )}
            </div>

            <ConfirmationModal isOpen={isConfirmModalOpen} isAdditional={isAdditionalActions} onClose={() => setIsConfirmModalOpen(false)} onSave={handleSave}
                selectedStudent={{ id: leadId, name: leadName, phone: leadPhone }} />

            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={isErrorToast}
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
};

export default AdmissionDetails;